import type { Assistant } from '../agent/assistant.ts';

/**
 * Zone 1 "Assistant" section: API-key entry (localStorage), chat log with
 * streaming text + tool activity + look() thumbnails, send/stop/clear.
 */

const KEY_STORAGE = 'aka-assistant-key';

export interface AssistantPanel {
  update: () => void;
}

export function createAssistantPanel(assistant: Assistant, hasTake: () => boolean): AssistantPanel {
  const panel = document.getElementById('assistant-panel') as HTMLElement;
  const keyInput = document.getElementById('assistant-key') as HTMLInputElement;
  const chat = document.getElementById('assistant-chat') as HTMLElement;
  const inputRow = document.getElementById('assistant-input-row') as HTMLElement;
  const input = document.getElementById('assistant-input') as HTMLTextAreaElement;
  const sendBtn = document.getElementById('assistant-send') as HTMLButtonElement;
  const stopBtn = document.getElementById('assistant-stop') as HTMLButtonElement;
  const clearBtn = document.getElementById('assistant-clear') as HTMLButtonElement;

  keyInput.value = localStorage.getItem(KEY_STORAGE) ?? '';
  keyInput.addEventListener('change', () => {
    localStorage.setItem(KEY_STORAGE, keyInput.value.trim());
    refreshVisibility();
  });

  function refreshVisibility(): void {
    const ready = keyInput.value.trim().length > 0;
    chat.hidden = !ready || chat.childElementCount === 0;
    inputRow.hidden = !ready;
  }

  function bubble(cls: string): HTMLElement {
    const el = document.createElement('div');
    el.className = `assistant-msg ${cls}`;
    chat.appendChild(el);
    chat.hidden = false;
    chat.scrollTop = chat.scrollHeight;
    return el;
  }

  let currentText: HTMLElement | null = null;

  function describeTool(name: string, toolInput: Record<string, unknown>): string {
    const args = Object.entries(toolInput)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k}: ${typeof v === 'number' ? +(+v).toFixed(3) : v}`)
      .join(', ');
    return `${name}(${args})`;
  }

  function run(): void {
    const text = input.value.trim();
    if (!text || assistant.busy()) return;
    if (!hasTake()) {
      bubble('error').textContent = 'Load a mocap take first.';
      return;
    }
    input.value = '';
    bubble('user').textContent = text;
    sendBtn.hidden = true;
    stopBtn.hidden = false;
    currentText = null;

    void assistant.send(text, {
      onMessageStart: () => {
        currentText = bubble('assistant');
      },
      onText: (delta) => {
        if (!currentText) currentText = bubble('assistant');
        currentText.textContent = (currentText.textContent ?? '') + delta;
        chat.scrollTop = chat.scrollHeight;
      },
      onTool: (name, toolInput) => {
        bubble('tool').textContent = `⚙ ${describeTool(name, toolInput)}`;
        currentText = null;
      },
      onImage: (dataUrl) => {
        const img = document.createElement('img');
        img.src = dataUrl;
        img.className = 'assistant-thumb';
        bubble('tool-img').appendChild(img);
        chat.scrollTop = chat.scrollHeight;
      },
      onError: (message) => {
        bubble('error').textContent = message;
      },
      onDone: () => {
        sendBtn.hidden = false;
        stopBtn.hidden = true;
        chat.scrollTop = chat.scrollHeight;
      },
    });
  }

  sendBtn.addEventListener('click', run);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      run();
    }
    e.stopPropagation(); // keep transport shortcuts from firing while typing
  });
  stopBtn.addEventListener('click', () => assistant.abort());
  clearBtn.addEventListener('click', () => {
    assistant.reset();
    chat.innerHTML = '';
    chat.hidden = true;
  });

  refreshVisibility();

  return {
    update: () => {
      panel.hidden = !hasTake();
    },
  };
}
